import React, { useEffect, useState, useRef } from 'react';
import { useBiltonAI } from '../contexts/BiltonAIContext';
import { Sparkles, Send, MessageSquare, RotateCcw, UserCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
type Role = 'All' | 'Executive' | 'Project Manager' | 'Superintendent' | 'Safety Manager';
const ROLES: Role[] = ['All', 'Executive', 'Project Manager', 'Superintendent', 'Safety Manager'];
interface Prompt {
  text: string;
  roles: Role[];
}
interface PromptGroup {
  title: string;
  prompts: Prompt[];
}
const ROLE_PROMPT_GROUPS: PromptGroup[] = [{
  title: '🔥 Most Popular',
  prompts: [{
    text: 'Which are the 3 projects most likely to have a safety incident?',
    roles: ['Executive', 'Safety Manager', 'Project Manager']
  }, {
    text: "Show me the worst safety analysis in today's preshift in my projects",
    roles: ['Superintendent', 'Safety Manager']
  }, {
    text: 'Tell me about weird behaviour of Headcount in my projects',
    roles: ['Executive', 'Project Manager']
  }]
}, {
  title: 'Safety & Risk',
  prompts: [{
    text: 'What are the top safety risks across all my sites this week?',
    roles: ['Executive', 'Safety Manager']
  }, {
    text: 'Which contractors have the most safety violations this month?',
    roles: ['Project Manager', 'Safety Manager']
  }, {
    text: 'Compare safety scores between my East Coast regions',
    roles: ['Executive']
  }]
}, {
  title: 'Documents & Compliance',
  prompts: [{
    text: 'Which sites have the most expired documents right now?',
    roles: ['Project Manager', 'Safety Manager']
  }, {
    text: 'Show me workers with expiring certifications in the next 7 days',
    roles: ['Superintendent', 'Safety Manager']
  }, {
    text: "What's our overall document compliance rate?",
    roles: ['Executive', 'Project Manager']
  }]
}, {
  title: 'Inspections & Logs',
  prompts: [{
    text: 'Which sites missed inspections this week?',
    roles: ['Superintendent', 'Project Manager']
  }, {
    text: "Summarize today's inspection findings across all projects",
    roles: ['Executive', 'Safety Manager']
  }, {
    text: 'Show me the trend of incomplete pre-shifts over the last month',
    roles: ['Superintendent', 'Safety Manager']
  }]
}, {
  title: 'Workers & Workforce',
  prompts: [{
    text: 'Which sites are understaffed compared to plan today?',
    roles: ['Project Manager', 'Superintendent']
  }, {
    text: 'Show me new workers who started this week across all sites',
    roles: ['Superintendent', 'Safety Manager']
  }, {
    text: "What's the average time workers spend on site before their first incident?",
    roles: ['Executive', 'Safety Manager']
  }]
}, {
  title: 'Trends & Analytics',
  prompts: [{
    text: 'How has our overall risk score changed over the past 4 weeks?',
    roles: ['Executive', 'Project Manager']
  }, {
    text: 'Which project type has the best safety record?',
    roles: ['Executive']
  }, {
    text: 'Give me a weekly safety summary I can share with leadership',
    roles: ['Project Manager', 'Safety Manager']
  }]
}];
export function BiltonAIPage() {
  const {
    messages,
    isTyping,
    sendMessage,
    clearMessages
  } = useBiltonAI();
  const [query, setQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<Role>('All');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);
  const handleSend = (text: string) => {
    if (!text.trim()) return;
    sendMessage(text);
    setQuery('');
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(query);
    }
  };
  const filteredGroups = ROLE_PROMPT_GROUPS.map(group => ({
    ...group,
    prompts: group.prompts.filter(p => selectedRole === 'All' || p.roles.includes(selectedRole))
  })).filter(group => group.prompts.length > 0);
  return <div className="min-h-screen bg-[#fafafa] font-sans flex flex-col" data-id="element-4098">
      <main className="flex-1 max-w-[1200px] w-full mx-auto p-6 flex flex-col h-[calc(100vh-60px)] overflow-hidden" data-id="element-4099">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 flex-1 flex flex-col overflow-hidden bg-gradient-to-br from-white to-indigo-50/20 min-h-0" data-id="element-4100">
          {/* Header */}
          <div className="px-6 py-4 border-b border-indigo-100 flex items-center justify-between bg-white/80 backdrop-blur-md z-10" data-id="element-4101">
            <div className="flex items-center gap-3" data-id="element-4102">
              <div className="bg-indigo-600 p-2 rounded-xl shadow-sm" data-id="element-4103">
                <Sparkles size={20} className="text-white" data-id="element-4104" />
              </div>
              <div data-id="element-4105">
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2" data-id="element-4106">
                  BiltonAI
                  <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100" data-id="element-4107">
                    Beta
                  </span>
                </h1>
                <p className="text-sm text-gray-500" data-id="element-4108">
                  Your intelligent construction safety assistant
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4" data-id="element-4109">
              {messages.length === 0 && <div className="flex items-center gap-2 bg-gray-50 p-1 rounded-lg border border-gray-200" data-id="element-4110">
                  <UserCircle2 size={16} className="text-gray-400 ml-2" data-id="element-4111" />
                  <span className="text-xs font-medium text-gray-500 mr-1" data-id="element-4112">
                    Role:
                  </span>
                  <div className="flex gap-1" data-id="element-4113">
                    {ROLES.map(role => <button key={role} onClick={() => setSelectedRole(role)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${selectedRole === role ? 'bg-white text-indigo-600 shadow-sm border border-gray-200' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-4114">
                        {role}
                      </button>)}
                  </div>
                </div>}

              {messages.length > 0 && <button onClick={clearMessages} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-indigo-50 hover:text-indigo-600 border border-gray-200 hover:border-indigo-200 rounded-lg transition-all" data-id="element-4115">
                  <RotateCcw size={16} data-id="element-4116" />
                  Start new chat
                </button>}
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6 min-h-0" data-id="element-4117">
            {messages.length === 0 ? <div className="max-w-4xl mx-auto flex flex-col" data-id="element-4118">
                <div className="text-center mb-6 pt-4" data-id="element-4119">
                  <div className="inline-flex bg-indigo-50 p-3 rounded-full mb-3" data-id="element-4120">
                    <Sparkles size={24} className="text-indigo-600" data-id="element-4121" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-4122">
                    How can I help you today?
                  </h2>
                  <p className="text-sm text-gray-500 max-w-md mx-auto" data-id="element-4123">
                    Select a suggested prompt based on your role, or type your
                    own question below.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-4" data-id="element-4124">
                  {filteredGroups.map((group, groupIndex) => <div key={groupIndex} className="space-y-2" data-id="element-4125">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2" data-id="element-4126">
                        {group.title}
                        <div className="h-px bg-gray-200 flex-1" data-id="element-4127"></div>
                      </h3>
                      <div className="space-y-1.5" data-id="element-4128">
                        {group.prompts.map((prompt, promptIndex) => <button key={promptIndex} onClick={() => handleSend(prompt.text)} className="w-full text-left p-3 rounded-xl border border-gray-200 bg-white hover:bg-indigo-50 hover:border-indigo-200 transition-all duration-200 text-sm text-gray-700 flex items-start gap-2.5 group shadow-sm hover:shadow" data-id="element-4129">
                            <MessageSquare size={15} className="text-indigo-400 mt-0.5 flex-shrink-0 group-hover:text-indigo-600 transition-colors" data-id="element-4130" />
                            <span className="leading-snug text-[13px]" data-id="element-4131">
                              {prompt.text}
                            </span>
                          </button>)}
                      </div>
                    </div>)}
                </div>
              </div> : <div className="max-w-4xl mx-auto space-y-6 pb-4" data-id="element-4132">
                {messages.map((msg, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-4133">
                    {msg.role === 'assistant' && <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center mr-3 flex-shrink-0 mt-1" data-id="element-4134">
                        <Sparkles size={14} className="text-white" data-id="element-4135" />
                      </div>}
                    <div className={`max-w-[80%] p-4 rounded-2xl text-[15px] leading-relaxed ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-sm shadow-md' : 'bg-white border border-gray-200 shadow-sm text-gray-800 rounded-tl-sm'}`} data-id="element-4136">
                      {msg.text}
                    </div>
                  </motion.div>)}

                {isTyping && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="flex justify-start" data-id="element-4137">
                    <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center mr-3 flex-shrink-0 mt-1" data-id="element-4138">
                      <Sparkles size={14} className="text-white" data-id="element-4139" />
                    </div>
                    <div className="bg-white border border-gray-200 shadow-sm p-4 rounded-2xl rounded-tl-sm flex items-center gap-1.5 h-[52px]" data-id="element-4140">
                      <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-4141" />
                      <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-4142" />
                      <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-4143" />
                    </div>
                  </motion.div>}
                <div ref={messagesEndRef} data-id="element-4144" />
              </div>}
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white border-t border-gray-200" data-id="element-4145">
            <div className="max-w-4xl mx-auto relative flex items-center" data-id="element-4146">
              <input type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ask BiltonAI anything about your projects..." className="w-full pl-5 pr-14 py-4 bg-gray-50 border border-gray-300 rounded-2xl text-[15px] focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all shadow-sm" data-id="element-4147" />
              <button onClick={() => handleSend(query)} disabled={!query.trim() || isTyping} className="absolute right-3 p-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:hover:bg-indigo-600 transition-colors shadow-sm" data-id="element-4148">
                <Send size={18} data-id="element-4149" />
              </button>
            </div>
            <div className="text-center mt-3" data-id="element-4150">
              <span className="text-xs text-gray-400" data-id="element-4151">
                BiltonAI can make mistakes. Check important safety information.
              </span>
            </div>
          </div>
        </div>
      </main>
    </div>;
}